import React, { useEffect, useState, useRef } from "react";
import { Avatar, Divider, Form, Select, DatePicker } from "antd";
import clsx from "clsx";
import { useIntl } from "react-intl";
import styled from "styled-components";
import {
  getInteraction,
  getOperationDashboard,
  toggleSpinner,
  getProgramById,
  getFinalReportMetric,
  getTotalSupport,
  getSupportedOpportunities,
  getApprovedOpportunityLocations,
  getPlannedIndicators,
  getPlannedStrategy,
  getPlannedBeneficiaries,
  getPlannedEnvironment,
  getAchievedIndicators,
  getAchievedStrategy,
  getAchievedBeneficiaries,
  getAchievedEnvironment,
} from "../../redux/actions";
import * as constants from "../../constants";
import { getSecuredUrl } from "../../utils/utilities";
import StyledText from "../../components/StyledText";
import MediaCarousel from "../../components/MediaCarousel";
import ThemePieChart from "../../components/ThemePieChart";
import BeneficiariesPieChart from "../../components/BeneficiariesPieChart";
import HorizontalGraph from "../../components/HorizontalGraph";
import ContentSkeleton from "./ContentSkeleton";
import {
  toggleSpinnerSelector,
  interactionSelector,
  portfolioDataSelector,
  programDataSelector,
  finalReportMetricsSelector,
  totalSupportSelector,
  supportedOpportunitiesSelector,
  approvedOpportunityLocationsSelector,
  plannedEnvironmentSelector,
  plannedBeneficiariesSelector,
  plannedStrategySelector,
  plannedIndicatorsSelector,
  achievedEnvironmentSelector,
  achievedBeneficiariesSelector,
  achievedStrategySelector,
  achievedIndicatorsSelector,
} from "../../redux/selectors";
import { useAppSelector, useAppDispatch } from "../../redux/store";
import Button from "../../components/Button";
import grantIcon from "../../assets/grant.svg";
import inKind from "../../assets/inkind.svg";
import volunteer from "../../assets/volunteer.svg";
import environmentIcon from "../../assets/environmentIcon.svg";
import LinkComponent from "../../components/LinkComponent";
import SkeletonView from "./Skeleton";
import programme from "../../assets/programme.svg";
import application from "../../assets/application.svg";
import finalReport from "../../assets/finalReport.svg";
import supportedProjects from "../../assets/supportedProjects.svg";
import grant from "../../assets/grant.svg";
import socialIcon from "../../assets/socialIcon.svg";
import MapBoxPointComponent from "../../components/MapBoxPointComponent";
import ImpactAlignmentComponent from "../../components/ImpactAlignmentComponent";

const aggregateEnvironmentalData = (environmentalData) => {
  if (!environmentalData || !Array.isArray(environmentalData)) {
    return [];
  }

  const aggregated = {};
  environmentalData.forEach((item) => {
    if (item && item.category && item.unit && item.amount && item.description) {
      const key = `${item.category}_${item.unit}_${item.description}`;
      if (!aggregated[key]) {
        aggregated[key] = {
          category: item.category,
          unit: item.unit,
          description: item.description,
          total: 0,
          maxValue: 0,
        };
      }
      const amount = parseFloat(item.amount);
      if (!isNaN(amount)) {
        aggregated[key].total += amount;
        aggregated[key].maxValue = Math.max(aggregated[key].maxValue, amount);
      }
    }
  });

  const columnsByCategoryUnit = {};
  Object.values(aggregated).forEach((item) => {
    const categoryUnitKey = `${item.category}_${item.unit}`;
    if (!columnsByCategoryUnit[categoryUnitKey]) {
      columnsByCategoryUnit[categoryUnitKey] = {
        category: item.category,
        unit: item.unit,
        data: [],
      };
    }
    columnsByCategoryUnit[categoryUnitKey].data.push({
      description: item.description,
      total: item.total,
      maxValue: item.maxValue,
    });
  });

  return Object.values(columnsByCategoryUnit);
};

const formItemLayout = {
  labelCol: { span: 2 },
  wrapperCol: { span: 20 },
};

function formatToYYYYMMDD(dateString) {
  const date = new Date(dateString);
  return (
    date.getUTCFullYear() +
    String(date.getUTCMonth() + 1).padStart(2, "0") +
    String(date.getUTCDate()).padStart(2, "0")
  );
}

const ProgramReview = ({ className }) => {
  const baseClassName = clsx("programReview", className);
  const intl = useIntl();
  const [form] = Form.useForm();
  const [isSticky, setIsSticky] = useState(false);
  const tabsContainerRef = useRef(null);
  const dispatch = useAppDispatch();
  const [selectedProject, setSelectedProject] = useState(null);
  const [formData, setFormData] = useState({});
  const [documentUrls, setDocumentUrls] = useState({});
  const [mediaImages, setMediaImages] = useState([]);
  const [showSkeleton, setShowSkeleton] = useState(false); // New state for skeleton loader
  const showSpinner = useAppSelector(toggleSpinnerSelector);
  const programData = useAppSelector(programDataSelector);
  const contentListData = useAppSelector(portfolioDataSelector) || "";
  const interactionsData = useAppSelector(interactionSelector);
  const finalReportMetricsData = useAppSelector(finalReportMetricsSelector);
  const totalSupportData = useAppSelector(totalSupportSelector);
  const supportedOpportunitiesData = useAppSelector(supportedOpportunitiesSelector);
  const approvedOpportunityLocationsData = useAppSelector(approvedOpportunityLocationsSelector);
  const plannedEnvironment = useAppSelector(plannedEnvironmentSelector);
  const plannedBeneficiariesData = useAppSelector(plannedBeneficiariesSelector);
  const plannedStrategy = useAppSelector(plannedStrategySelector);
  const plannedIndicators = useAppSelector(plannedIndicatorsSelector);
  const achievedEnvironment = useAppSelector(achievedEnvironmentSelector);
  const achievedBeneficiaries = useAppSelector(achievedBeneficiariesSelector);
  const achievedStrategy = useAppSelector(achievedStrategySelector);
  const achievedIndicators = useAppSelector(achievedIndicatorsSelector);

  const [selectedPoint, setSelectedPoint] = useState("");

  const { data: { submittedfinalreports } = {} } = finalReportMetricsData || {};
  const { data } = approvedOpportunityLocationsData || {};

  const { data: plannedIndicatorsData } = plannedIndicators || {};

  const { data: { grants, inkind, volunteering } = {} } = totalSupportData || {};

  const { data: plannedEnvironmentData = {} } = plannedEnvironment || {};
  const { data: { indirect, direct, types } = {} } = plannedBeneficiariesData || {};

  const { data: programAnalyticsDashboardData = {} } = plannedStrategy || {};

  const { data: { supportedOpportunities, totalApplications } = {} } = supportedOpportunitiesData || {};

  const { data: achievedEnvironmentData = {} } = achievedEnvironment || {};
  const {
    data: { indirect: achievedBeneficiariesIndirect, direct: achievedBeneficiariesDirect, types: achievedBeneficiariesTypes } = {},
  } = achievedBeneficiaries || {};
  const { data: achievedProgramAnalyticsDashboardData = {} } = achievedStrategy || {};
  const { data: achievedIndicatorsData } = achievedIndicators || {};

  const { reportType, programID, startDate, closedDate } = formData || {};
  const { programSummary, currencySymbol } = programData || {};

  const groupedEnvironmentalData = aggregateEnvironmentalData(plannedEnvironmentData);
  const achievedGroupedEnvironmentalData = aggregateEnvironmentalData(achievedEnvironmentData);

  const handlePointClick = (point) => {
    setSelectedPoint(point?.opportunityID);
  };

  const overviewRef = useRef(null);
  const plannedImpactRef = useRef(null);
  const achievedImpactRef = useRef(null);
  const contentRef = useRef(null);
  const [activeTab, setActiveTab] = useState("summary");

  const scrollToSection = (ref, tab) => {
    setActiveTab(tab);
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  const separateBySpecificTypes = (data) => {
    if (!data?.length) return;
    return data?.reduce(
      (acc, item) => {
        if (["Grant", "Volunteering", "In-Kind"]?.includes(item.type)) {
          acc[item.type]?.push(item);
        }
        return acc;
      },
      { Grant: [], Volunteering: [], "In-Kind": [] }
    );
  };

  const { Grant, Volunteering, "In-Kind": InKind } = separateBySpecificTypes(selectedProject?.donations) || {};

  const { opportunitySummary, gallery, caseStudy } = selectedProject || {};

  const handleSetSelectedProject = (project) => {
    setShowSkeleton(true);
    setSelectedProject(project);
    setTimeout(() => {
      setShowSkeleton(false);
    }, 1000);
  };

  useEffect(() => {
    if (submittedfinalreports || contentListData?.length > 0) {
      if (contentListData?.length > 0) {
        const firstProgramName = contentListData?.[0];
        setSelectedProject(firstProgramName);
      }
      dispatch(toggleSpinner(false));
    }
  }, [submittedfinalreports, contentListData]);

  useEffect(() => {
    const handleScroll = () => {
      if (tabsContainerRef.current) {
        if (window.pageYOffset > 200) {
          setIsSticky(true);
        } else {
          setIsSticky(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    dispatch(
      getInteraction({
        sortBy: "programName",
        asc: "true",
        filterBy: {
          status: ["Approved", "Completed"],
        },
        limit: "100",
      })
    );
  }, []);

  useEffect(() => {
    const fetchDocumentUrls = async () => {
      try {
        if (caseStudy?.length > 0) {
          const promises = caseStudy.map(async (el) => {
            if (el?.documents?.length > 0 && (el?.outcome || el?.outcome) && !documentUrls[el.outcome]?.length) {
              const documentPromises = el.documents.map(async (doc) => {
                const key = doc.key || doc.fileKey;
                const securedURI = await getSecuredUrl(key);
                const securedURL = URL.createObjectURL(securedURI);
                return { url: securedURL, name: doc.name, type: doc.type };
              });

              const resolvedDocuments = await Promise.all(documentPromises);
              return { outcome: el.outcome, documents: resolvedDocuments.filter(Boolean) };
            }
            return null;
          });

          const resolvedDocumentData = await Promise.all(promises);
          const validDocuments = resolvedDocumentData.filter(Boolean);

          setDocumentUrls((prev) => ({
            ...prev,
            ...validDocuments.reduce((acc, { outcome, documents }) => {
              acc[outcome] = documents;
              return acc;
            }, {}),
          }));
        }
      } catch (error) {
        console.error("Error fetching document URLs:", error);
      }
    };

    fetchDocumentUrls();
  }, [caseStudy]);

  useEffect(() => {
    const fetchMediaImages = async () => {
      try {
        if (gallery?.length > 0 && !mediaImages?.length) {
          const promises = gallery.map(async (file) => {
            const key = file?.key || file?.fileKey;
            const securedURI = await getSecuredUrl(key);
            const securedURL = URL.createObjectURL(securedURI);
            return { url: securedURL, name: file.name, mediaDescription: file.mediaDescription };
          });
          const resolvedMediaImages = await Promise.all(promises);
          const validMediaImages = resolvedMediaImages.filter(Boolean);
          setMediaImages(validMediaImages);
        }
      } catch (error) {
        console.error("Error fetching media images:", error);
      }
    };

    fetchMediaImages();
  }, [gallery]);

  const onFinishFailed = (e) => {
    console.log("Failed:", e);
  };

  const onFormChange = (_, allValues) => {
    setFormData(allValues);
  };

  const onFinish = async (e) => {
    const { startDate, closedDate } = form.getFieldsValue();

    if (startDate && !closedDate) {
      form.setFields([{ name: "closedDate", errors: ["Please select an end date if a start date is chosen."] }]);
      return;
    }

    if (closedDate && !startDate) {
      form.setFields([{ name: "startDate", errors: ["Please select a start date if an end date is chosen."] }]);
      return;
    }

    if (startDate && closedDate && !closedDate.isAfter(startDate)) {
      form.setFields([{ name: "closedDate", errors: ["End date must be after the start date."] }]);
      return;
    }

    if (programID || reportType || (startDate && closedDate)) {
      dispatch(toggleSpinner(true));
      const commonParams = {
        programID,
        openDate: startDate ? formatToYYYYMMDD(startDate) : "",
        closeDate: closedDate ? formatToYYYYMMDD(closedDate) : "",
      };

      if (programID?.length === 1) {
        dispatch(getProgramById(programID[0]));
      }

      const metricRequests = [
        { action: "overview", metric: "reportssubmitted", fn: getFinalReportMetric },
        { action: "overview", metric: "donations", fn: getTotalSupport },
        { action: "overview", metric: "counts", fn: getSupportedOpportunities },
        { action: "overview", metric: "approvedOpportunityLocations", fn: getApprovedOpportunityLocations },
        { action: "planned", metric: "plannedIndicators", fn: getPlannedIndicators },
        { action: "planned", metric: "plannedStrategy", fn: getPlannedStrategy },
        { action: "planned", metric: "plannedBeneficiaries", fn: getPlannedBeneficiaries },
        { action: "planned", metric: "plannedEnvironment", fn: getPlannedEnvironment },
        { action: "achieved", metric: "achievedIndicators", fn: getAchievedIndicators },
        { action: "achieved", metric: "achievedStrategy", fn: getAchievedStrategy },
        { action: "achieved", metric: "achievedBeneficiaries", fn: getAchievedBeneficiaries },
        { action: "achieved", metric: "achievedEnvironment", fn: getAchievedEnvironment },
      ];

      const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      const batchSize = 3;

      for (let i = 0; i < metricRequests.length; i += batchSize) {
        const batch = metricRequests.slice(i, i + batchSize);
        batch.forEach(({ action, metric, fn }) => {
          dispatch(fn({ ...commonParams, action, metric }));
        });
        if (i + batchSize < metricRequests.length) {
          await delay(500);
        }
      }

      dispatch(
        getOperationDashboard({
          ...commonParams,
          action: "content",
        })
      );
    }
  };

  return (
    <div className={baseClassName}>
      <div className="leftSubContainer">
        <div className="subContentContainer">
          <StyledText as="p" variant="H2" className="title">
            {intl.formatMessage({ id: "consolidated_report" })}
          </StyledText>
          <Form
            {...formItemLayout}
            labelAlign="left"
            form={form}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            className="formContainer"
            scrollToFirstError
            onValuesChange={onFormChange}
          >
            <div className="dropDownSubContainer">
              <StyledText as="p" variant="B3d" className="dropdownLabel">
                {intl.formatMessage({ id: "portfolio" })}
              </StyledText>
              <Form.Item name="portfolio">
                <Select
                  disabled
                  allowClear
                  className="selectBox"
                  placeholder={intl.formatMessage({ id: "portfolio_name" })}
                  type="formSelect"
                  options={[]}
                />
              </Form.Item>
            </div>
            <div className="dropDownSubContainer">
              <StyledText as="p" variant="B3d" className="dropdownLabel">
                {intl.formatMessage({ id: "select_programme" })}
              </StyledText>
              <Form.Item name="programID">
                <Select
                  allowClear
                  className="selectBox"
                  placeholder={intl.formatMessage({ id: "select_programme" })}
                  type="formSelect"
                  mode="multiple"
                  options={interactionsData
                    ?.map((el) => ({
                      key: el?.programID,
                      label: el?.programName,
                      value: el?.programID,
                    }))
                    .filter((program, index, self) => index === self.findIndex((p) => p.label === program.label))}
                />
              </Form.Item>
            </div>
            <div className="dropDownSubContainer">
              <StyledText as="p" variant="B3d" className="dropdownLabel">
                {intl.formatMessage({ id: "period" })}
              </StyledText>
              <div className="dateContainer">
                <Form.Item name="startDate" className="formItem smallInput selectBox">
                  <DatePicker format="YYYY-MM-DD" placeholder="Enter start date" />
                </Form.Item>
                <div className="dropDownSubContainer">
                  <Form.Item name="closedDate" className="formItem smallInput selectBox">
                    <DatePicker format="YYYY-MM-DD" placeholder="Enter end date" />
                  </Form.Item>
                </div>
                <Button variant="primary" htmlType="submit">
                  <StyledText as="p" variant="B3d" className="generate">
                    {intl.formatMessage({ id: "generate" })}
                  </StyledText>
                </Button>
              </div>
            </div>
          </Form>
        </div>
        {submittedfinalreports && (
          <div ref={tabsContainerRef} className={clsx("tabsContainer", { sticky: isSticky })}>
            <button
              className={`tabButton ${activeTab === "overview" ? "active" : ""}`}
              onClick={() => scrollToSection(overviewRef, "overview")}
            >
              Overview
            </button>
            <button
              className={`tabButton ${activeTab === "plannedImpact" ? "active" : ""}`}
              onClick={() => scrollToSection(plannedImpactRef, "plannedImpact")}
            >
              Planned Impact
            </button>
            <button
              className={`tabButton ${activeTab === "achievedImpact" ? "active" : ""}`}
              onClick={() => scrollToSection(achievedImpactRef, "achievedImpact")}
            >
              Achieved Impact
            </button>
            <button
              className={`tabButton ${activeTab === "content" ? "active" : ""}`}
              onClick={() => scrollToSection(contentRef, "content")}
            >
              Content
            </button>
          </div>
        )}
        {showSpinner ? (
          <SkeletonView />
        ) : (
          submittedfinalreports &&
          programID?.length > 0 && (
            <>
              <StyledText ref={overviewRef} as="p" variant="H1" className="overview">
                {intl.formatMessage({ id: "overview" })}
              </StyledText>
              <StyledText as="p" variant="H2">{intl.formatMessage({ id: "summary_title" })}</StyledText>
              <StyledText as="p" variant="B3">
                {programID?.length === 1 ? programSummary : intl.formatMessage({ id: "impact_across_multiple_programmes" })}
              </StyledText>
              <div className="progressTopContainer">
                <div className="progressContainer">
                  <StyledText as="p" variant="H2">{intl.formatMessage({ id: "progress" })}</StyledText>
                  <div className="itemCardContainer">
                    <div className="itemCard">
                      <div className="itemCardImage">
                        <Avatar src={programme} />
                      </div>
                      <div>
                        <StyledText as="p" variant="B3d">{intl.formatMessage({ id: "programme" })}</StyledText>
                        <StyledText as="p" variant="T3">{programID?.length}</StyledText>
                      </div>
                    </div>
                    <div className="itemCard">
                      <div className="itemCardImage">
                        <Avatar src={application} />
                      </div>
                      <div>
                        <StyledText as="p" variant="B3d">{intl.formatMessage({ id: "applications" })}</StyledText>
                        <StyledText as="p" variant="T3">{totalApplications}</StyledText>
                      </div>
                    </div>
                  </div>

                  <StyledText as="p" variant="H2">{intl.formatMessage({ id: "engagements" })}</StyledText>
                  <div className="itemCardContainer">
                    <div className="itemCard">
                      <div className="itemCardImage">
                        <Avatar src={finalReport} />
                      </div>
                      <div>
                        <StyledText as="p" variant="B3d">{intl.formatMessage({ id: "final_reports" })}</StyledText>
                        <StyledText as="p" variant="T3">{submittedfinalreports}</StyledText>
                      </div>
                    </div>
                    <div className="itemCard">
                      <div className="itemCardImage">
                        <Avatar src={supportedProjects} />
                      </div>
                      <div>
                        <StyledText as="p" variant="B3d">{intl.formatMessage({ id: "supported_projects" })}</StyledText>
                        <StyledText as="p" variant="T3">{supportedOpportunities}</StyledText>
                      </div>
                    </div>
                  </div>
                  <StyledText as="p" variant="H2">{intl.formatMessage({ id: "total_support" })}</StyledText>
                  <div className="itemCardContainer">
                    <div className="itemCard grantContainer">
                      <div className="itemCardSupport">
                        <Avatar src={grant} />
                        <StyledText as="p" variant="B3d">{intl.formatMessage({ id: "grants" })}</StyledText>
                      </div>
                      <div>
                        <StyledText as="p" variant="B3">{intl.formatMessage({ id: "dispersed_amount" })}</StyledText>
                        <StyledText as="p" variant="B3c">{`${currencySymbol} ${grants}`}</StyledText>
                      </div>
                    </div>
                    <div className="itemCard volunteeringContainer">
                      <div className="itemCardSupport">
                        <Avatar src={volunteer} />
                        <StyledText as="p" variant="B3d">{intl.formatMessage({ id: "volunteering" })}</StyledText>
                      </div>
                      <div>
                        <StyledText as="p" variant="B3">{intl.formatMessage({ id: "resource_used" })}</StyledText>
                        <StyledText as="span" variant="B3c">{`${volunteering} ${intl.formatMessage({ id: "days" })}`}</StyledText>
                      </div>
                    </div>
                    <div className="itemCard inKindContainer">
                      <div className="itemCardSupport">
                        <Avatar src={inKind} />
                        <StyledText as="p" variant="B3d">{intl.formatMessage({ id: "inkind" })}</StyledText>
                      </div>
                      <div>
                        <StyledText as="p" variant="B3">{intl.formatMessage({ id: "dispersed_amount" })}</StyledText>
                        <StyledText as="p" variant="B3c">{`${currencySymbol} ${inkind}`}</StyledText>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <StyledText as="p" variant="H2" style={{ marginBottom: "15px" }}>
                    {intl.formatMessage({ id: "approved_projects" })}
                  </StyledText>
                  <div className="mapBoxContainer">
                    <MapBoxPointComponent points={data} selectedPointId={selectedPoint} onPointClick={handlePointClick} mapStyle="mapbox://styles/mapbox/outdoors-v12" />
                  </div>
                </div>
              </div>
              <div ref={plannedImpactRef}></div>
              <StyledText as="p" variant="H1">{intl.formatMessage({ id: "planned_impact_title" })}</StyledText>
              <Divider style={{ borderWidth: "3px" }} />
              {programAnalyticsDashboardData?.themes && (
                <>
                  <StyledText as="p" variant="H2">{intl.formatMessage({ id: "themes_support" })}</StyledText>
                  <div className="pieChartContainer cardContainer">
                    <ThemePieChart themes={programAnalyticsDashboardData?.themes} />
                  </div>
                </>
              )}
              {programAnalyticsDashboardData?.impactAlignment && (
                <>
                  <StyledText as="p" variant="H2">{intl.formatMessage({ id: "impact_alignment" })}</StyledText>
                  <ImpactAlignmentComponent
                    className="impactAlignment"
                    impactAlignment={programAnalyticsDashboardData?.impactAlignment}
                    outcomeMethod={programAnalyticsDashboardData?.outcomeMethod}
                  />
                </>
              )}
              <StyledText as="p" variant="H2">{intl.formatMessage({ id: "beneficiaries" })}</StyledText>
              {types?.length > 0 && (
                <div className="cardContainer halfContainer">
                  <div className="titleContainer">
                    <div>
                      <Avatar src={socialIcon} />
                    </div>
                    <div>
                      <StyledText as="p" variant="H3">{intl.formatMessage({ id: "social_beneficiaries" })}</StyledText>
                    </div>
                  </div>
                  <div className="progressParentContainer">
                    <div className="progressParentContainer">
                      <StyledText as="p" variant="B3">{intl.formatMessage({ id: "direct" })}</StyledText>
                      <StyledText as="p" variant="B3c">{direct}</StyledText>
                    </div>
                    <div className="progressParentContainer">
                      <StyledText as="p" variant="B3">{intl.formatMessage({ id: "indirect" })}</StyledText>
                      <StyledText as="p" variant="B3c">{indirect}</StyledText>
                    </div>
                  </div>
                  <div className="pieChartContainer">
                    <BeneficiariesPieChart data={types} />
                  </div>
                </div>
              )}
              {groupedEnvironmentalData && Object.entries(groupedEnvironmentalData)?.length > 0 && (
                <div className="cardContainer">
                  <div className="titleContainer">
                    <div>
                      <Avatar src={environmentIcon} />
                    </div>
                    <div>
                      <StyledText as="p" variant="H3">{intl.formatMessage({ id: "environmental_beneficiaries" })}</StyledText>
                    </div>
                  </div>
                  <div className="environmentalBeneficiaries">
                    {groupedEnvironmentalData.map((column, index) => (
                      <div key={`${column.category}_${column.unit}_${index}`} className="categoryContainer">
                        <StyledText as="p" variant="B3" style={{ fontWeight: "bold", marginBottom: "15px" }}>
                          {column.category} ({column.unit || "N/A"})
                        </StyledText>
                        <div className="progressChildContainer">
                          {column.data.map((item, idx) => (
                            <HorizontalGraph
                              key={`${column.category}_${item.description}_${idx}`}
                              name={item.description}
                              percentage={item.total || 0}
                              maxValue={item.maxValue || 1}
                              background="#5b81ed"
                              titleStyle={{
                                color: "black",
                                fontSize: "14px",
                                fontWeight: 400,
                                marginBottom: 0,
                                width: "100px",
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {plannedIndicatorsData?.length > 0 && (
                <>
                  <StyledText as="p" variant="H2">{intl.formatMessage({ id: "indicators" })}</StyledText>
                  <div className="cardContainer">
                    <div className="indicatorContainer">
                      <div className="progressChildContainer">
                        {(Array.isArray(plannedIndicatorsData) ? plannedIndicatorsData : []).map((item, index, arr) => {
                          const maxValue = (() => {
                            const validSums = arr.map((el) => el?.sum).filter((sum) => sum != null);
                            return validSums.length ? Math.max(...validSums) : 1;
                          })();
                          return (
                            item && (
                              <HorizontalGraph
                                key={`${item.unit}_${index}`}
                                name={item.name}
                                percentage={item.sum || 0}
                                maxValue={maxValue}
                                background="#5b81ed"
                                titleStyle={{
                                  color: "black",
                                  fontSize: "14px",
                                  fontWeight: 400,
                                  marginBottom: 0,
                                  width: "100px",
                                }}
                              />
                            )
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </>
              )}
              <StyledText ref={achievedImpactRef} as="p" variant="H1">
                {intl.formatMessage({ id: "achieved_impact_title" })}
              </StyledText>
              <Divider style={{ borderWidth: "3px" }} />
              {achievedProgramAnalyticsDashboardData?.themes && (
                <>
                  <StyledText as="p" variant="H2">{intl.formatMessage({ id: "themes_support" })}</StyledText>
                  <div className="pieChartContainer cardContainer">
                    <ThemePieChart themes={achievedProgramAnalyticsDashboardData?.themes} />
                  </div>
                </>
              )}
              {achievedProgramAnalyticsDashboardData?.impactAlignment && (
                <>
                  <StyledText as="p" variant="H2">{intl.formatMessage({ id: "impact_alignment" })}</StyledText>
                  <ImpactAlignmentComponent
                    className="impactAlignment"
                    impactAlignment={achievedProgramAnalyticsDashboardData?.impactAlignment}
                    outcomeMethod={achievedProgramAnalyticsDashboardData?.outcomeMethod}
                  />
                </>
              )}
              <StyledText as="p" variant="H2">{intl.formatMessage({ id: "beneficiaries" })}</StyledText>
              {achievedBeneficiariesTypes?.length > 0 && (
                <div className="cardContainer halfContainer">
                  <div className="titleContainer">
                    <div>
                      <Avatar src={socialIcon} />
                    </div>
                    <div>
                      <StyledText as="p" variant="H3">{intl.formatMessage({ id: "social_beneficiaries" })}</StyledText>
                    </div>
                  </div>
                  <div className="progressParentContainer">
                    <div className="progressParentContainer">
                      <StyledText as="p" variant="B3">{intl.formatMessage({ id: "direct" })}</StyledText>
                      <StyledText as="p" variant="B3c">{achievedBeneficiariesDirect}</StyledText>
                    </div>
                    <div className="progressParentContainer">
                      <StyledText as="p" variant="B3">{intl.formatMessage({ id: "indirect" })}</StyledText>
                      <StyledText as="p" variant="B3c">{achievedBeneficiariesIndirect}</StyledText>
                    </div>
                  </div>
                  <div className="pieChartContainer">
                    <BeneficiariesPieChart data={achievedBeneficiariesTypes} />
                  </div>
                </div>
              )}
              {achievedGroupedEnvironmentalData && achievedGroupedEnvironmentalData.length > 0 && (
                <div className="cardContainer">
                  <div className="titleContainer">
                    <div>
                      <Avatar src={environmentIcon} />
                    </div>
                    <div>
                      <StyledText as="p" variant="H3">{intl.formatMessage({ id: "environmental_beneficiaries" })}</StyledText>
                    </div>
                  </div>
                  <div className="environmentalBeneficiaries">
                    {achievedGroupedEnvironmentalData.map((column, index) => (
                      <div key={`${column.category}_${column.unit}_${index}`} className="categoryContainer">
                        <StyledText as="p" variant="B3" style={{ fontWeight: "bold", marginBottom: "15px" }}>
                          {column.category} ({column.unit || "N/A"})
                        </StyledText>
                        <div className="progressChildContainer">
                          {column.data.map((item, idx) => (
                            <HorizontalGraph
                              key={`${column.category}_${item.description}_${idx}`}
                              name={item.description}
                              percentage={item.total || 0}
                              maxValue={item.maxValue || 1}
                              background="#5b81ed"
                              titleStyle={{
                                color: "black",
                                fontSize: "14px",
                                fontWeight: 400,
                                marginBottom: 0,
                                width: "100px",
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {achievedIndicatorsData?.length > 0 && (
                <>
                  <StyledText as="p" variant="H2">{intl.formatMessage({ id: "indicators" })}</StyledText>
                  <div className="cardContainer">
                    <div className="indicatorContainer">
                      <div className="progressChildContainer">
                        {(Array.isArray(achievedIndicatorsData) ? achievedIndicatorsData : []).map((item, index, arr) => {
                          const maxValue = (() => {
                            const validSums = arr.map((el) => el?.sum).filter((sum) => sum != null);
                            return validSums.length ? Math.max(...validSums) : 1;
                          })();
                          return (
                            item && (
                              <HorizontalGraph
                                key={`${item.unit}_${index}`}
                                name={item.name}
                                percentage={item.sum || 0}
                                maxValue={maxValue}
                                background="#5b81ed"
                                titleStyle={{
                                  color: "black",
                                  fontSize: "14px",
                                  fontWeight: 400,
                                  marginBottom: 0,
                                  width: "100px",
                                }}
                              />
                            )
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </>
              )}
              {contentListData?.length > 0 && (
                <div ref={contentRef} className="parentContainer">
                  {showSkeleton ? <ContentSkeleton /> : <div className="leftSubContainer">
                    <StyledText as="p" variant="H1" className="allProjects divider">
                      {intl.formatMessage({ id: "project_reports" })}
                    </StyledText>
                    <Divider style={{ borderWidth: "3px" }} />
                    <div className="opportunityName">
                      <StyledText as="p" variant="H2">{selectedProject?.opportunityName}</StyledText>
                      <LinkComponent
                        href={`${window.location.origin}/company/content/${selectedProject?.contentID}?type=final`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <StyledText as="p" variant="B3">{intl.formatMessage({ id: "see_full_report" })}</StyledText>
                      </LinkComponent>
                    </div>

                    <div className="subContainer">
                      <StyledText as="p" variant="H2">{intl.formatMessage({ id: "summary_title" })}</StyledText>
                      <StyledText as="p" variant="B3">{opportunitySummary}</StyledText>
                    </div>
                    <div className="subContainer">
                      <StyledText as="p" variant="H2">{intl.formatMessage({ id: "support_awarded" })}</StyledText>
                      <div className="supportFlexContainer">
                        {Grant?.length > 0 && (
                          <div className="supportContainer grantContainer">
                            <div className="titleContainer">
                              <div>
                                <Avatar src={grantIcon} />
                              </div>
                              <div>
                                <StyledText as="p" variant="H3">{intl.formatMessage({ id: "grants" })}</StyledText>
                              </div>
                            </div>
                            <div className="flexBox">
                              <div className="flexBoxChild">
                                <div className="flexColumn">
                                  {Grant?.map((el) => (
                                    <StyledText key={el?.donationAmount?.trim()} as="p" variant="B3c">
                                      {el?.currencySymbol || "$"}
                                      {el?.donationAmount?.trim()}
                                    </StyledText>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {Volunteering?.length > 0 && (
                          <div className="supportContainer volunteeringContainer">
                            <div className="titleContainer">
                              <div>
                                <Avatar src={volunteer} />
                              </div>
                              <div>
                                <StyledText as="p" variant="H3">{intl.formatMessage({ id: "volunteering" })}</StyledText>
                              </div>
                            </div>
                            <ul>
                              {Volunteering.map((el) => (
                                <li key={el?.donationAmount}>
                                  <StyledText as="span" variant="B3c">{`${el?.donationAmount} ${intl.formatMessage({ id: "days" })}`}</StyledText>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {InKind?.length > 0 && (
                          <div className="supportContainer inKindContainer">
                            <div className="titleContainer">
                              <div>
                                <Avatar src={inKind} />
                              </div>
                              <div>
                                <StyledText as="p" variant="H3">{intl.formatMessage({ id: "inKind" })}</StyledText>
                              </div>
                            </div>
                            <ul>
                              {InKind?.map((el) => (
                                <li key={el?.inKindActivityDescription}>
                                  <StyledText as="span" variant="B3">
                                    {`${el?.inkindDescription} ${intl.formatMessage({ id: "of_equivalent_value" })}`}
                                    <StyledText as="span" variant="B3c">{` ${el.currencySymbol}${el?.donationAmount}`}</StyledText>
                                  </StyledText>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="subContainer">
                      {caseStudy?.map((el) => el?.name && (
                        <div className="subWhiteContainer" key={(el?.outcome || el?.outcome) || el?.name}>
                          <StyledText
                            as="p"
                            variant="B3c"
                            style={{
                              background: "#CBEAFF",
                              borderRadius: "4px",
                              width: "max-content",
                              paddingInline: "4px",
                            }}
                          >
                            {`${intl.formatMessage({ id: "case_study" })}`}
                          </StyledText>
                          <div className="contentContainer">
                            <div className="leftContent">
                              <StyledText as="p" variant="B3" style={{ fontSize: "16px", color: "#759CC7" }}>
                                {el.name}
                              </StyledText>
                              <StyledText as="p" variant="B3d">{intl.formatMessage({ id: "description" })}</StyledText>
                              <StyledText as="p" variant="B3">{el?.outcome}</StyledText>
                              <div className="quoteContainer">
                                <StyledText as="p" variant="B3" style={{ fontStyle: "italic" }}>
                                  {el?.shareQuote}
                                </StyledText>
                                <StyledText as="p" variant="B3" style={{ fontStyle: "italic" }}>
                                  {el?.quoteBy || el?.quoteby}
                                </StyledText>
                              </div>
                            </div>
                            <div className="mediaContainer">
                              {(el?.outcome || el?.outcome) && documentUrls[el.outcome]?.length === el?.documents?.length && (
                                <MediaCarousel data={documentUrls[el.outcome]} />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {Array.isArray(gallery) && gallery?.length > 0 && (
                      <div className="subContainer mediaSection">
                        <StyledText as="p" variant="H2">{intl.formatMessage({ id: "media" })}</StyledText>
                        {mediaImages?.length === gallery?.length && <MediaCarousel data={mediaImages} />}
                      </div>
                    )}
                  </div>}
                  <div className="rightSubContainer">
                    <StyledText as="p" variant="H1" className="allProjects">
                      {intl.formatMessage({ id: "project_reports" })}
                    </StyledText>
                    <Divider style={{ borderWidth: "3px" }} />
                    <div className="mainContent">
                      {contentListData?.length > 0 &&
                        contentListData?.map((el, index) => (
                          <div key={`${el?.opportunityName}-${el?.programName}-${index}`}>
                            <button className="programNameButton" onClick={() => handleSetSelectedProject(el)}>
                              <StyledText as="p" variant="H3" className="subTitle">
                                {el?.opportunityName}
                              </StyledText>
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
};

const StyledProgramReview = styled(ProgramReview)`
  &.programReview {
    display: flex;
    gap: 24px;
    padding-bottom: 60px;
    padding: 20px;
    background: #f2f4f8;
    .cardContainer {
      display: flex;
      gap: 20px;
      flex-direction: column;
      background: white;
      padding: 30px;
      border-radius: 10px;
      width: max-content;
    }

    .halfContainer {
      width: 100%;
    }

    .progressTopContainer {
      display: flex;
      gap: 20px;
    }
    .categoryContainer {
      width: 400px;
    }

    .programReview {
      .cardContainer {
        display: flex;
        gap: 20px;
        flex-direction: column;
        background: white;
        padding: 30px;
        border-radius: 10px;
        width: max-content;
      }
      .progressParentContainer {
        display: flex;
        gap: 10px;
        flex-direction: column;
      }
    }
    .progressChildContainer {
      display: flex;
      flex-direction: column;
    }
    .generate {
      color: white;
    }

    .progressParentContainer {
      display: flex;
      gap: 30px;
      flex-wrap: wrap;
      width: 80vw;
    }
    .progressContainer {
      display: flex;
      gap: 30px;
      flex-direction: column;
      width: 41%;
    }

    .itemCardContainer {
      display: flex;
      gap: 24px;
    }

    .itemCardSupport {
      display: flex;
      gap: 5px;
      align-items: center;
      justify-content: center;
    }

    .itemCard {
      display: flex;
      align-items: center;
      padding: 15px;
      width: 248px;
      height: max-content;
      box-shadow: 0px 3px 16px #0000000d;
      border-radius: 10px;
      gap: 15px;
      .itemCardImage {
        width: 48px;
        height: 48px;
        box-shadow: 0px 3px 16px #0000000d;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      &.grantContainer {
        background: transparent linear-gradient(223deg, #ffffff 0%, #b2f9bd 100%) 0% 0% no-repeat padding-box;
        box-shadow: 0px 3px 16px #0000000d;
        border-radius: 10px;
        gap: 16px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }
      &.volunteeringContainer {
        background: transparent linear-gradient(223deg, #ffffff 0%, #8bdaff 100%) 0% 0% no-repeat padding-box;
        box-shadow: 0px 3px 16px #0000000d;
        border-radius: 10px;
        gap: 16px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }
      &.inKindContainer {
        background: transparent linear-gradient(223deg, #ffffff 0%, #ffaffa 100%) 0% 0% no-repeat padding-box;
        box-shadow: 0px 3px 16px #0000000d;
        border-radius: 10px;
        gap: 16px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }
    }
    .mapBoxContainer {
      position: relative;
      height: 95%;
      width: 50vw;
    }
    .pieChartContainer {
      height: 400px;
      width: max-content;
    }

    .tabsContainer {
      display: flex;
      gap: 16px;
      padding: 0 20px;
      background: white;
      border-radius: 8px;
      box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
      overflow-x: auto;
      z-index: 9;
      transition: all 0.3s ease; /* Smooth transition for sticky header */
      &.sticky {
        position: fixed;
        top: 61px;
        width: calc(100% - 40px);
      }
    }

    .tabButton {
      padding: 12px 16px;
      background: transparent;
      border: none;
      border-bottom: 3px solid transparent;
      cursor: pointer;
      white-space: nowrap;
      font-weight: 500;
      color: #666;
      transition: all 0.3s ease;

      &:hover {
        color: #0469ac;
      }

      &.active {
        color: #0469ac;
        border-bottom: 3px solid #0469ac;
        font-weight: 600;
      }
    }

    .opportunityName {
      background: rgba(4, 105, 172, 0.1);
      border-radius: 10px;
      padding: 5px 10px;
      display: flex;
      justify-content: space-between;
      &.skeleton {
        padding: 20px;
      }
    }
    .mainContent {
      overflow-y: auto;
      height: 75%;
      &.skeleton {
        padding: 20px;
      }
    }
    .parentContainer {
      display: flex;
      gap: 24px;
      &.skeleton {
        padding: 20px;
      }
    }
    .allProjects {
      padding-top: 10px;
      &.divider {
        margin-bottom: -20px;
      }
    }
    .rightSubContainer {
      height: 60vh;
      overflow-y: scroll;
      gap: 10px;
      display: flex;
      flex-direction: column;
      background: white;
      box-shadow: 0px 3px 16px #0000000d;
      border-radius: 10px;
      display: flex;
      flex-direction: column;
      padding: 0 16px;
      width: 20%;
      &.skeleton {
        padding: 20px;
      }
      .programNameButton {
        width: 100%;
        display: flex;
        text-align: left;
        background: transparent;
        border: none;
        cursor: pointer;
        text-decoration: underline;
        &.skeleton {
          padding: 20px;
        }
        &:hover {
          color: #04ac9c;
          background: #f2fdf9;
          border-radius: 10px;
          p {
            color: #04ac9c;
            background: #f2fdf9;
          }
        }
      }
    }

    .subContentContainer {
      background: transparent linear-gradient(136deg, #000f19 0%, #0469ac 100%) 0% 0% no-repeat padding-box;
      box-shadow: 0px 3px 16px #0000000d;
      border-radius: 10px;
      padding: 20px;
      &.skeleton {
        padding: 20px;
      }
    }

    .dropdownLabel {
      color: #fff;
      &.skeleton {
        padding: 20px;
      }
    }

    .dropDownSubContainer {
      display: flex;
      flex-direction: column;
      gap: 10px;
      .ant-form-item {
        width: 250px !important;
      }
      .ant-picker {
        width: 200px !important;
      }
    }

    .title {
      margin-bottom: 15px;
      font-size: 20px;
      font-family: ${constants.POPPINS_FONT_FAMILY};
      font-weight: 600;
      color: #ffffff;
      &.skeleton {
        padding: 20px;
      }
    }

    .contentContainer {
      display: flex;
      justify-content: space-between;
      &.skeleton {
        padding: 20px;
      }
      .leftContent {
        display: flex;
        flex-direction: column;
        gap: 10px;
        &.skeleton {
          padding: 20px;
        }
      }
      .mediaContainer {
        width: 20vw;
        .carouselImage {
          width: 100%;
          height: 150px;
          object-fit: contain;
        }
        .ant-carousel,
        .slick-initialized {
          height: 150px;
        }
        .ant-carousel .slick-dots-bottom {
          bottom: 0 !important;
        }
      }
    }

    .formContainer {
      display: flex;
      gap: 50px;
      &.skeleton {
        padding: 20px;
      }
    }

    .dateContainer {
      display: flex;
      gap: 50px;
    }
    .supportParentContainer {
      display: flex;
      gap: 10px;
    }
    .switchItemContainer {
      width: 100%;
      display: flex;
      justify-content: flex-end;

      .switchItem {
        width: max-content;
      }
    }

    .quoteContainer {
      border-left: 2px solid black;
      padding: 5px 10px;
    }

    .overlay {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1000;
      height: 100%;
      background-color: rgba(255, 255, 255, 0.45);
    }
    .spinner {
      z-index: 2000;
      margin: auto;
      left: 50%;
      right: 50%;
      position: absolute;
      bottom: 50%;
    }
    ul {
      margin: 0;
    }

    .leftSubContainer {
      width: 95%;
      display: flex;
      flex-direction: column;
      gap: 30px;
      &.skeleton {
        padding: 20px;
      }
    }

    .whiteSupportContainer {
      background: white;
      box-shadow: 0px 3px 16px #0000000d;
      border-radius: 10px;
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding: 16px;
      width: 35vw;
      &.volunteeringContainer {
        box-shadow: 0px 3px 16px #0000000d;
        border-radius: 10px;
        display: flex;
        flex-direction: column;
        gap: 16px;
      }
    }

    .ant-collapse-expand-icon {
      color: #313131 !important;
    }

    .mediaSection {
      margin-bottom: 20px;
    }

    .flexBox {
      display: flex;
      align-items: flex-start;
      gap: 20px;
      margin-left: 35px;
      &.justifyContent {
        justify-content: flex-start;
      }
      .flexBoxChild {
        display: flex;
        flex-direction: column;
        gap: 10px;
        .flexColumn {
          display: flex;
          flex-direction: column;
          .noBreak {
            white-space: nowrap;
          }
        }
      }
    }

    .divider {
      height: 100%;
      margin-inline: 10px;
    }
    .outputNumber {
      background: #dee3ed;
      border-radius: 5px;
      padding: 1px 8px;
      border: 1px solid grey;
    }

    .supportFlexContainer {
      display: flex;
      gap: 30px;
      flex-direction: column;

      .supportContainer {
        padding: 16px;
        width: max-content;
        width: 35vw;

        &.grantContainer {
          background: transparent linear-gradient(223deg, #ffffff 0%, #b2f9bd 100%) 0% 0% no-repeat padding-box;
          box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 0px;
          &.skeleton {
            padding: 20px;
          }
        }

        &.volunteeringContainer {
          background: transparent linear-gradient(223deg, #ffffff 0%, #8bdaff 100%) 0% 0% no-repeat padding-box;
          box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 0px;
          &.skeleton {
            padding: 20px;
          }
        }

        &.inKindContainer {
          background: transparent linear-gradient(223deg, #ffffff 0%, #ffaffa 100%) 0% 0% no-repeat padding-box;
          box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          &.skeleton {
            padding: 20px;
          }
        }
      }
    }
    .titleContainer {
      display: flex;
      gap: 2px;
      align-items: center;
      &.skeleton {
        padding: 20px;
      }
    }
    .helperText {
      color: #5271ff !important;
    }

    .subContainer {
      display: flex;
      flex-direction: column;
      gap: 10px;
      &.skeleton {
        padding: 20px;
      }
      .descriptionContainer {
        max-width: 70ch;
        word-wrap: break-word;
        overflow-wrap: break-word;
        &.skeleton {
          padding: 20px;
        }
        p,
        li {
          font-size: 14px;
          font-weight: 500;
          color: #242424;
          font-family: sans-serif;
          max-width: 70ch;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
      }
    }

    .subWhiteContainer {
      display: flex;
      flex-direction: column;
      gap: 10px;
      background: white;
      border-radius: 10px;
      padding: 10px;
      &.skeleton {
        padding: 20px;
      }
    }

    .ant-collapse-expand-icon {
      transform: rotate(90deg) translate(-5px);
      svg {
        fill: #313131 !important;
      }
    }

    .ant-collapse-item-active {
      .ant-collapse-expand-icon {
        transform: rotate(0deg) translate(-5px);
      }
    }

    .ant-timeline-item-head {
      background: transparent !important;
    }

    .anticon-check {
      color: #58b55e;
    }
    .supportContainer {
      padding: 16px;
      width: max-content;
      width: 35vw;
      .divider {
        height: 100%;
        margin-inline: 10px;
      }
      &.grantContainer {
        background: transparent linear-gradient(223deg, #ffffff 0%, #b2f9bd 100%) 0% 0% no-repeat padding-box;
        box-shadow: 0px 3px 16px #0000000d;
        border-radius: 10px;
        display: flex;
        flex-direction: column;
        gap: 0px;
        &.skeleton {
          padding: 20px;
        }
      }
    }
    .additionalFunding {
      background: white !important;
      padding: 20px;
      border-radius: 10px;
      display: flex;
      flex-direction: column;
      gap: 20px;
    }
    .expenditureValues {
      background: #5db76a;
      border-radius: 5px;
      padding: 1px 8px;
      border: 1px solid #5db76a;
    }
    .supportContainer {
      padding: 16px;
      width: max-content;
      width: 35vw;
      .divider {
        height: 100%;
        margin-inline: 10px;
      }
      &.volunteeringContainer {
        background: transparent linear-gradient(223deg, #ffffff 0%, #8bdaff 100%) 0% 0% no-repeat padding-box;
        box-shadow: 0px 3px 16px #0000000d;
        border-radius: 10px;
        display: flex;
        flex-direction: column;
        gap: 0px;
      }
    }
    .titleContainer {
      display: flex;
      gap: 2px;
      align-items: center;
    }
    .supportContainer {
      padding: 16px;
      width: max-content;
      width: 35vw;
      &.inKindContainer {
        background: transparent linear-gradient(223deg, #ffffff 0%, #ffaffa 100%) 0% 0% no-repeat padding-box;
        box-shadow: 0px 3px 16px #0000000d;
        border-radius: 10px;
      }
    }
    .outputNumber {
      background: #dee3ed;
      border-radius: 5px;
      padding: 1px 8px;
      border: 1px solid grey;
    }
    .unsdgPartitionContainer {
      display: flex;
      gap: 20px;
      background: white;
      padding: 30px;
      border-radius: 10px;

      .collapseContainer {
        width: 50%;
        button {
          width: 100%;
        }
      }
      .unsdgsContainerContainer {
        display: flex;
        flex-direction: column;
        gap: 15px;
        width: 48%;
      }
      .unsdgsContainer {
        display: flex;
        gap: 15px;
        flex-wrap: wrap;
        height: max-content;

        .showAllGoals {
          height: 100px;
          width: 100px;
          background: white;
          display: flex;
          flex-direction: column;
          padding: 15px;
          justify-content: center;
          align-items: center;
          text-align: center;
          border: 2px solid #00877a;
          border-radius: 6px;

          p,
          svg {
            color: #00877a !important;
          }
        }

        .unsdgs {
          height: 100px;
          width: 100px;
        }
      }
    }

    .expandAll {
      color: #00877a;
      text-align: end;
      background: transparent;
      border: 0;
      cursor: pointer;
    }

    .ant-collapse {
      border: 0;
      border-bottom: 1px solid #d7dde1;
      .ant-collapse-header {
        background: white !important;
      }
    }
    .ant-collapse-content-active {
      border-top: 0;
    }
    .collapseChildContainer {
      border: 1px solid #d7dde1;
      padding: 12px 16px;
      margin-bottom: 6px;
      cursor: pointer;
      text-align: left;
      background: white;
      font-size: 14px;
      font-weight: ${constants.FONT_WEIGHT_SEMIBOLD};
      color: ${constants.COLOR_GREY_DARK};
      font-family: ${constants.POPPINS_FONT_FAMILY};

      &.selectedName {
        border: 1px solid #04ac9c;
        border-radius: 6px;
        background: #bcfbf5;
      }
    }

    .collapsible {
      display: flex;
      flex-direction: column;

      .collapseChildContainer {
        background: white;
        border: 0;
        border-bottom: 1px solid #d9d9d9;
        padding: 12px 16px;
        text-align: left;

        &.selectedName {
          border: 1px solid #04ac9c;
          background: #bcfbf5;
        }

        cursor: pointer;
      }
    }
    .ant-collapse-expand-icon {
      transform: rotate(90deg) translate(-5px);
      svg {
        fill: #313131 !important;
      }
    }

    .ant-collapse-item-active {
      .ant-collapse-expand-icon {
        transform: rotate(0deg) translate(-5px);
      }
    }
    .environmentalBeneficiaries {
      display: flex;
      column-gap: 50px;
      row-gap: 50px;
      flex-wrap: wrap;
      width: 80vw;
    }
    .indicatorContainer {
      width: 80vw;
    }
  }
`;

export default StyledProgramReview;